import { isBrowser } from '../misc';
import { getLastUsedPosition, ExpireablePosition } from '../geo/userLocation';

import { getBusinessDayForRegion } from '../date/businessDay';
import { distance } from '../geo';
import {
  OpeningHours,
  OpeningTimeInfo,
  TimingList,
  Timing,
  UserLocationInfo,
  LatLong,
  StringMap,
  TLD,
} from '../../types';

type DaysOfWeek = { [key in TLD]: StringMap<string> };

export const daysOfTheWeek: DaysOfWeek = {
  [TLD.DE]: {
    0: 'Montag',
    1: 'Dienstag',
    2: 'Mittwoch',
    3: 'Donnerstag',
    4: 'Freitag',
    5: 'Samstag',
    6: 'Sonntag',
  },
  [TLD.FR]: {
    0: 'lundi',
    1: 'mardi',
    2: 'mercredi',
    3: 'jeudi',
    4: 'vendredi',
    5: 'samedi',
    6: 'dimanche',
  },
  [TLD.NL]: {
    0: 'Maandag',
    1: 'Dinsdag',
    2: 'Woensdag',
    3: 'Donderdag',
    4: 'Vrijdag',
    5: 'Zaterdag',
    6: 'Zondag',
  },
};

export const translatedDaysOfWeek = (tld: TLD) => daysOfTheWeek[tld];

export const MAX_EXPIRE_TIME_MS = 24 * 60 * 60 * 1000; // 24H

export const FALLBACK_POSITION_MAP: { [key in TLD]: UserLocationInfo } & { default: UserLocationInfo } = {
  [TLD.DE]: { coords: { lat: 50.841601, lng: 9.990768 }, zipCode: '36269', city: 'Philippsthal' }, // Philippsthal, center of Germany
  [TLD.FR]: { coords: { lat: 48.860519, lng: 2.339164 }, zipCode: '75003', city: 'Paris' }, // Paris
  [TLD.NL]: { coords: { lat: 50.841601, lng: 9.990768 }, zipCode: '36269', city: 'Philippsthal' }, // Philippsthal, center of Germany
  default: { coords: { lat: 52.510457, lng: 13.409633 }, zipCode: '10115', city: 'Berlin' }, // Berlin
};

export const getFallbackPosition = (tld: TLD) => FALLBACK_POSITION_MAP[tld] || FALLBACK_POSITION_MAP.default;

export const getOpeningTimeInfo = (tld: TLD, currentTime: Date, timeTable?: OpeningHours): OpeningTimeInfo | null => {
  const currentRegion = tld;
  const nextRange =
    timeTable != null &&
    Object.keys(timeTable ?? []).length > 0 &&
    getBusinessDayForRegion(currentTime, timeTable, currentRegion);

  if (!nextRange) {
    return null;
  }
  const [nextOpenDayTime, nextClosingDayTime] = nextRange;
  const now = currentTime.getTime();
  const isOpenNow = now < nextClosingDayTime.getTime() && now > nextOpenDayTime.getTime();
  return { isOpenNow, nextOpenDayTime };
};

export const getStoreTimingOfEachDay = (tld: TLD, timeRanges: string[][] | null): TimingList | null => {
  const timings: Timing[] =
    timeRanges == null
      ? []
      : timeRanges.reduce((acc, [starts, ends], index) => {
          if (starts == null || ends == null) {
            return acc;
          }
          const newEntry = {
            opensAt: starts.substring(0, starts.lastIndexOf(':')),
            closesAt: ends.substring(0, ends.lastIndexOf(':')),
            day: daysOfTheWeek[tld][index],
          };

          acc.push(newEntry);
          return acc;
        }, [] as Timing[]);

  return tld === TLD.DE ? { unit: 'Uhr', data: timings } : ({ data: timings } as TimingList);
};

export const getAvailablePosition = (position: ExpireablePosition | null): UserLocationInfo | null => {
  if (!position || !position.timeStamp || new Date().getTime() - position.timeStamp > MAX_EXPIRE_TIME_MS) {
    return null;
  }
  return ['coords', 'zipCode', 'city'].reduce((acc, key) => ({ ...acc, [key]: position[key] }), {} as UserLocationInfo);
};

export function retrieveLastPosition() {
  if (isBrowser()) {
    return getAvailablePosition(getLastUsedPosition());
  } else {
    return null;
  }
}

export function getPosition(tld: TLD) {
  try {
    const currentPosition = retrieveLastPosition();
    if (currentPosition != null) {
      return currentPosition;
    }
  } catch (e) {
    console.error(`Could not find current location: ${e.message}`, e);
  }
  return getFallbackPosition(tld);
}

export const getDistanceInKm = (tld: TLD, coords: LatLong) => {
  if (coords) {
    const position = getPosition(tld);
    try {
      const distanceInKm = distance(coords, position.coords) / 1000;
      return distanceInKm ? distanceInKm : 0;
    } catch (e) {
      console.error(`Could not find distance to shop: ${e.message}`, e);
    }
  }
  return 0;
};

export const getCurrentTime = () => new Date();
